import styles from "./css/CameraTable.module.css";
import { ColorClass, Header2, SquareContainer } from "ui";
import { CameraData } from "@web/types/CameraData";
import { getCameraDataForDisplay } from "@web/utils/camera/getCameraDataForDisplay";
import { imageDimensionsFromUrl } from "@web/utils/camera/imageDimensionsFromUrl";
import Image from "next/image";
import { CameraDetail } from "@web/components/camera/CameraDetail";
import { CameraSelect } from "@web/components/select/CameraSelect";
import { useReducer } from "react";
import Link from "next/link";
import { cameraNameForUrl } from "@web/utils/camera/cameraNameForUrl";

const MAX_COLUMNS = 3;
const NULL_COLUMN = "---";

type CameraNamesDispatchAction = {
  cameraName: string;
  index: number;
  type: "set_index";
};

function cameraNamesReducer(
  state: Array<string>,
  action: CameraNamesDispatchAction
): Array<string> {
  const newState = [...state];
  newState[action.index] = action.cameraName;

  const oldIndex = state.findIndex(
    (cameraName) => cameraName === action.cameraName
  );
  if (oldIndex === -1) {
    return newState;
  }

  newState[oldIndex] = state[action.index];
  return newState;
}

type Props = {
  cameraDataArray: Array<CameraData>;
};

export function CameraTable({ cameraDataArray }: Props) {
  const dataForDisplay = cameraDataArray.map((cameraData) =>
    getCameraDataForDisplay(cameraData, NULL_COLUMN)
  );

  const allCameraNames = cameraDataArray.map((camera) => camera["Camera Name"]);
  // TODO: may want better way to limit number of cameras
  const cameraNamesInitial = allCameraNames.slice(0, MAX_COLUMNS);
  const [cameraNames, dispatchCameraNames] = useReducer<
    typeof cameraNamesReducer
  >(cameraNamesReducer, cameraNamesInitial);

  // Prepare the rows, which are the properties
  const properties = Object.keys(dataForDisplay[0]);

  // Create an array of row objects. Each object has a property for each camera,
  // and the value of the property is the value of the corresponding camera property
  const transposedData = properties.map((property) => {
    const row = { property };
    cameraNames.forEach((cameraName, i) => {
      // @ts-ignore
      row[cameraName] = dataForDisplay[i][property];
    });
    return row;
  });

  return (
    <table className={styles.table}>
      <thead>
        <tr>
          {cameraNames
            .map(
              (cameraName) =>
                cameraDataArray.find(
                  (data) => data["Camera Name"] === cameraName
                )!
            )
            .map((cameraData, i) => {
              const imageDimensions = imageDimensionsFromUrl(
                cameraData["Link to Image"]
              );
              return (
                <th className={styles.th} key={i}>
                  <CameraSelect
                    cameraNames={allCameraNames}
                    onChange={(cameraName) =>
                      dispatchCameraNames({
                        cameraName,
                        index: i,
                        type: "set_index",
                      })
                    }
                    value={cameraData["Camera Name"]}
                  />
                  <Link
                    href={`/cameras/${cameraNameForUrl(
                      cameraData["Camera Name"]
                    )}`}
                    style={{ textDecoration: "none" }}
                  >
                    <div
                      className={styles.imageContainer}
                      style={{ backgroundColor: cameraData["Color"] }}
                    >
                      <SquareContainer
                        classNameContainerInner={styles.imageSquareContainer}
                      >
                        <Image
                          alt={cameraData["Camera Name"]}
                          className={styles.image}
                          height={imageDimensions.height}
                          src={cameraData["Link to Image"]}
                          style={{ objectFit: "contain" }}
                          width={imageDimensions.width}
                        />
                      </SquareContainer>
                    </div>
                    <Header2
                      className={styles.cameraName}
                      colorClass={ColorClass.Primary}
                    >
                      {cameraData["Camera Name"]}
                    </Header2>
                  </Link>
                </th>
              );
            })}
        </tr>
      </thead>
      <tbody className={styles.section}>
        {transposedData.map((row, i) => {
          // If no cameras have the feature, just don't show it
          if (
            Object.values(row)
              .slice(1)
              .every((value) => value === NULL_COLUMN)
          ) {
            return null;
          }
          return (
            <tr key={i}>
              {cameraNames.map((cameraName, j) => (
                <td key={j} className={styles.td}>
                  <CameraDetail
                    // @ts-ignore
                    labelAndValue={row[cameraName]}
                    // @ts-ignore
                    omitLabel={row[cameraName].label === "Description"}
                  />
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
